import React, { useState, useEffect } from 'react'
import { BeakerIcon, CheckCircleIcon, PlayIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'

const SCENARIOS = [
  { id: 'bill_stuffing',     label: 'Bill stuffing',     desc: 'CashIn >> Turnover' },
  { id: 'device_rush',       label: 'Device rush',       desc: '5+ EGMs in 30 min' },
  { id: 'session_duration',  label: 'Session duration',  desc: 'Session > 6 hours' },
  { id: 'off_hours',         label: 'Off-hours',         desc: 'Activity 2AM–5AM' },
  { id: 'rapid_play',        label: 'Rapid play',        desc: 'Short session + high turnover → rapid_play_flag' },
  { id: 'extended_play',     label: 'Extended play',     desc: '5h continuous session → extended_play_flag' },
  { id: 'frequency_pattern', label: 'Frequency pattern', desc: '6 sessions in 40 min (attributed only)' },
  { id: 'pep_patron',        label: 'PEP patron',        desc: 'Session attributed to seeded PEP member (SAMPLE-002)' },
]

const ATTRIBUTED_ONLY_SCENARIOS = new Set(['frequency_pattern', 'pep_patron'])

function SampleDataGenerator() {
  const [date, setDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return d.toISOString().slice(0, 10)
  })
  const [includeMetering, setIncludeMetering] = useState(true)
  const [includeGaming, setIncludeGaming] = useState(true)
  const [autoEnrich, setAutoEnrich] = useState(true)
  const [simulations, setSimulations] = useState({})
  const [status, setStatus] = useState(null)
  const [isLoadingStatus, setIsLoadingStatus] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isGeneratingSims, setIsGeneratingSims] = useState(false)
  const [isEnriching, setIsEnriching] = useState(false)
  const [lastResult, setLastResult] = useState(null)

  const fetchStatus = async () => {
    setIsLoadingStatus(true)
    try {
      const resp = await authFetch(getLocalApiUrl('/api/sample-data/status'))
      const json = await resp.json()
      if (json?.success) setStatus(json.data)
      else setStatus(null)
    } catch {
      setStatus(null)
    } finally {
      setIsLoadingStatus(false)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  const toggleSim = (scenarioId, key) => {
    setSimulations(prev => {
      const s = prev[scenarioId] || {}
      return { ...prev, [scenarioId]: { ...s, [key]: !s[key] } }
    })
  }

  const enrichedSummary = (data) => {
    if (!data?.enrich) return ''
    const s = data.enrich
    return ` • enriched ${s.sessions_enriched ?? 0}` +
      (s.sessions_no_meter_in_window ? `, skipped ${s.sessions_no_meter_in_window}` : '')
  }

  const handleGenerateBaseline = async () => {
    setIsGenerating(true)
    setLastResult(null)
    try {
      const resp = await authFetch(getLocalApiUrl('/api/sample-data/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          mode: 'normal',
          includeMetering,
          includeGaming,
          simulations: [],
          autoEnrich,
        })
      })
      const json = await resp.json()
      if (json?.success) {
        setLastResult(json.data)
        toast.success(`Generated ${json.data.metersInserted} meters, ${json.data.sessionsInserted} sessions${enrichedSummary(json.data)}`)
        fetchStatus()
      } else {
        toast.error(json?.error || 'Generate failed')
      }
    } catch (e) {
      toast.error(e.message || 'Generate failed')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleGenerateSimulations = async () => {
    const sims = []
    for (const [scenarioId, opts] of Object.entries(simulations)) {
      // Attribution-only scenarios: ignore the "without member" toggle.
      if (ATTRIBUTED_ONLY_SCENARIOS.has(scenarioId)) {
        if (opts?.withMember || opts?.withoutMember) {
          sims.push({ scenario: scenarioId, attributed: true })
        }
        continue
      }
      if (opts?.withMember) sims.push({ scenario: scenarioId, attributed: true })
      if (opts?.withoutMember) sims.push({ scenario: scenarioId, attributed: false })
    }
    if (sims.length === 0) {
      toast.error('Select at least one simulation (With member / Without member)')
      return
    }
    setIsGeneratingSims(true)
    setLastResult(null)
    try {
      const resp = await authFetch(getLocalApiUrl('/api/sample-data/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          mode: 'simulations',
          includeMetering: true,
          includeGaming: true,
          simulations: sims,
          autoEnrich,
        })
      })
      const json = await resp.json()
      if (json?.success) {
        setLastResult(json.data)
        toast.success(`Generated ${json.data.sessionsInserted} sessions, ${json.data.metersInserted} meters${enrichedSummary(json.data)}`)
        fetchStatus()
      } else {
        toast.error(json?.error || 'Generate failed')
      }
    } catch (e) {
      toast.error(e.message || 'Generate failed')
    } finally {
      setIsGeneratingSims(false)
    }
  }

  // Phase C: on-demand enrichment runner. Useful after tweaking rules config
  // without regenerating data, or to re-enrich sessions whose context has
  // changed (e.g. after updating a SAMPLE patron's risk profile).
  const handleEnrichNow = async () => {
    setIsEnriching(true)
    try {
      const resp = await authFetch(getLocalApiUrl('/api/gaming/sessions/enrich'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ only_new: false })
      })
      const json = await resp.json()
      if (json?.success) {
        const s = json.enrich || {}
        toast.success(`Enriched ${s.sessions_enriched ?? 0} sessions` +
          (s.sessions_no_meter_in_window ? `, skipped ${s.sessions_no_meter_in_window}` : ''))
        fetchStatus()
      } else {
        toast.error(json?.error || 'Enrich failed')
      }
    } catch (e) {
      toast.error(e.message || 'Enrich failed')
    } finally {
      setIsEnriching(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center gap-2 mb-6">
        <BeakerIcon className="w-8 h-8 text-indigo-600" />
        <h1 className="text-2xl font-semibold text-gray-900">Sample Data Generator</h1>
      </div>
      <p className="text-gray-600 mb-6">
        Generate correlated metering and gaming data for testing rule triggers. Use SAMPLE-* members and SAMPLE_VENUE.
      </p>

      {/* Status */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-medium text-gray-900">Status</h2>
          <button
            onClick={handleEnrichNow}
            disabled={isEnriching}
            className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
            title="Run /api/gaming/sessions/enrich now (Phase C pipeline)"
          >
            {isEnriching ? 'Enriching…' : 'Enrich now'}
          </button>
        </div>
        {isLoadingStatus ? (
          <p className="text-gray-500">Loading…</p>
        ) : status ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
            <span>Enabled: {status.enabled ? 'Yes' : 'No'}</span>
            <span>Meters (SAMPLE-*): {status.counts?.meters ?? 0}</span>
            <span>Sessions (SAMPLE-*): {status.counts?.sessions ?? 0}</span>
            <span>Enriched (SAMPLE-*): {status.counts?.enriched ?? '—'}</span>
            <span>EGM registry (SAMPLE-*): {status.counts?.egm_registry ?? '—'}</span>
            <span>Patron profiles (SAMPLE-*): {status.counts?.patron_profiles ?? '—'}</span>
          </div>
        ) : (
          <p className="text-amber-600">Could not load status</p>
        )}
      </div>

      {/* Date & Include */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 w-48"
          />
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeMetering}
              onChange={e => setIncludeMetering(e.target.checked)}
            />
            <span>Metering (egm_meter_readings)</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeGaming}
              onChange={e => setIncludeGaming(e.target.checked)}
            />
            <span>Gaming (gaming_sessions)</span>
          </label>
          <label className="flex items-center gap-2" title="After generating, automatically run /api/gaming/sessions/enrich so rows land in gaming_session_enriched with member/risk/behaviour context (Phase C pipeline).">
            <input
              type="checkbox"
              checked={autoEnrich}
              onChange={e => setAutoEnrich(e.target.checked)}
            />
            <span>Auto-enrich after generate</span>
          </label>
        </div>
      </div>

      {/* Generate baseline */}
      <div className="mb-8">
        <button
          onClick={handleGenerateBaseline}
          disabled={isGenerating || (!includeMetering && !includeGaming)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlayIcon className="w-5 h-5" />
          {isGenerating ? 'Generating…' : 'Generate data for this day'}
        </button>
        <p className="text-sm text-gray-500 mt-2">Creates baseline correlated metering + gaming data.</p>
      </div>

      {/* Issue simulations */}
      <div className="border-t pt-6">
        <h2 className="font-medium text-gray-900 mb-3">Issue simulations</h2>
        <p className="text-sm text-gray-500 mb-4">
          Select scenarios to generate rule-triggering data. With member = attributed (MemberID); Without member = unattributed.
        </p>
        <div className="space-y-4">
          {SCENARIOS.map(s => {
            const attributedOnly = ATTRIBUTED_ONLY_SCENARIOS.has(s.id)
            return (
              <div key={s.id} className="flex flex-wrap items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1 min-w-[180px]">
                  <span className="font-medium">{s.label}</span>
                  <span className="text-gray-500 text-sm ml-2">— {s.desc}</span>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={simulations[s.id]?.withMember ?? false}
                    onChange={() => toggleSim(s.id, 'withMember')}
                  />
                  <span>{attributedOnly ? 'Enable' : 'With member'}</span>
                </label>
                {!attributedOnly && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={simulations[s.id]?.withoutMember ?? false}
                      onChange={() => toggleSim(s.id, 'withoutMember')}
                    />
                    <span>Without member</span>
                  </label>
                )}
              </div>
            )
          })}
        </div>
        <button
          onClick={handleGenerateSimulations}
          disabled={isGeneratingSims}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
        >
          <PlayIcon className="w-5 h-5" />
          {isGeneratingSims ? 'Generating…' : 'Generate selected simulations'}
        </button>
      </div>

      {lastResult && (
        <div className="mt-6 p-4 bg-green-50 rounded-lg flex items-start gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">Last run</p>
            <p className="text-sm text-green-700">
              Date: {lastResult.date} — Meters: {lastResult.metersInserted}, Sessions: {lastResult.sessionsInserted}
              {lastResult.simulations > 0 && `, Simulations: ${lastResult.simulations}`}
              {typeof lastResult.egmSeeded === 'number' && `, EGM seeded: ${lastResult.egmSeeded}`}
              {typeof lastResult.patronSeeded === 'number' && `, Patrons seeded: ${lastResult.patronSeeded}`}
            </p>
            {lastResult.enrich && (
              <p className="text-sm text-green-700 mt-1">
                Enriched: {lastResult.enrich.sessions_enriched ?? 0}
                {lastResult.enrich.sessions_no_meter_in_window ? `, skipped: ${lastResult.enrich.sessions_no_meter_in_window}` : ''}
                {lastResult.enrich.sessions_with_patron_profile != null && `, w/ patron profile: ${lastResult.enrich.sessions_with_patron_profile}`}
                {lastResult.enrich.sessions_rapid_play != null && `, rapid_play: ${lastResult.enrich.sessions_rapid_play}`}
                {lastResult.enrich.sessions_extended_play != null && `, extended_play: ${lastResult.enrich.sessions_extended_play}`}
              </p>
            )}
            {lastResult.autoEnrich === false && (
              <p className="text-xs text-amber-700 mt-1">
                Auto-enrich was disabled — click <strong>Enrich now</strong> above to populate gaming_session_enriched.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SampleDataGenerator
